/**
 * Phone-number entry for the onboarding picker, shared by web and native.
 *
 * Both apps used to hard-code NANP rules — a fixed ten digits, `###-###-####`
 * grouping — so a German mobile (ten or eleven national digits, written with a
 * leading trunk `0`) could not be entered in a shape the backend would accept
 * (BIG-7175). libphonenumber-js supplies the per-country length rules, trunk
 * prefix, and grouping instead, so no numbering plan is spelled out by hand.
 */

import {
  AsYouType,
  getCountryCallingCode,
  isSupportedCountry,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import type { CountryConfig } from "./countryList.ts";

/** E.164 caps a number at 15 digits including the country code. */
const E164_MAX_DIGITS = 15;

/** Shortest national number any country assigns, used only by the fallback path. */
const FALLBACK_MIN_DIGITS = 7;

/**
 * ISO-3166 alpha-2 code for a country, read off its flag emoji: 🇩🇪 is the
 * regional indicators for D and E. Every entry in {@link countryList} carries a
 * well-formed flag, which is why the codes aren't duplicated as their own field.
 *
 * Returns undefined for a flag that isn't exactly two regional indicators.
 */
export const iso2FromFlag = (flag: string) => {
  const points = [...flag].map((char) => char.codePointAt(0) ?? 0);
  if (points.length !== 2) return undefined;
  if (points.some((point) => point < 0x1f1e6 || point > 0x1f1ff)) return undefined;
  return points.map((point) => String.fromCharCode(point - 0x1f1e6 + 65)).join("");
};

/**
 * The country to parse against, or undefined when libphonenumber has no
 * numbering plan for it — several entries in the picker are territories with no
 * assigned numbers of their own (Antarctica, Bouvet Island, the US Minor
 * Outlying Islands).
 */
const parseCountry = (country: CountryConfig) => {
  const iso2 = iso2FromFlag(country.flag);
  return iso2 !== undefined && isSupportedCountry(iso2) ? iso2 : undefined;
};

const digitsOf = (value: string) => value.replace(/\D/g, "");

/**
 * The country's E.164 calling code, digits only.
 *
 * Not simply the picker's `code`: dependent territories carry an area code in
 * theirs — Guernsey is `+44-1481` — and those trailing digits belong to the
 * national number, not the country code. Counting them twice made a valid
 * Guernsey number look longer than E.164 allows.
 */
const callingCodeOf = (country: CountryConfig) => {
  const iso2 = parseCountry(country);
  if (iso2 !== undefined) return getCountryCallingCode(iso2);
  return digitsOf(country.code.split("-")[0] ?? country.code);
};

/**
 * Best-effort E.164 for a country libphonenumber doesn't model. Strips the
 * national trunk prefix and accepts any plausible length, which is all we can
 * assert without a numbering plan — the alternative is making those countries
 * unselectable.
 */
const fallbackE164 = (nationalInput: string, country: CountryConfig) => {
  const digits = digitsOf(nationalInput).replace(/^0+/, "");
  const callingCode = callingCodeOf(country);
  if (digits.length < FALLBACK_MIN_DIGITS) return null;
  if (callingCode.length + digits.length > E164_MAX_DIGITS) return null;
  return `+${callingCode}${digits}`;
};

/**
 * The E.164 number to submit, or null while the input isn't a valid number for
 * the selected country. Callers gate their submit button on this, so it doubles
 * as the validity check.
 *
 * A number typed with its own `+` country code wins over the picker, matching
 * how every other phone field behaves.
 *
 * The return type is spelled out rather than inferred because libphonenumber
 * brands `parsed.number` as `E164Number`; callers only ever need the string.
 */
export const toE164 = (nationalInput: string, country: CountryConfig): string | null => {
  const iso2 = parseCountry(country);
  if (iso2 === undefined) return fallbackE164(nationalInput, country);

  const parsed = parsePhoneNumberFromString(nationalInput, iso2);
  return parsed?.isValid() === true ? parsed.number : null;
};

/** A phone field's starting state: the picker country and the number as typed. */
export type PhonePrefill = { countryName: string; national: string };

/**
 * The picker country and formatted national number for an E.164 number okthx
 * already knows, so a form can start filled in. The inverse of {@link toE164}.
 *
 * Only the digits are read, with the `+` put back: a `+` in a query string
 * decodes to a space unless it was percent-encoded. Where several countries
 * share a calling code (`+1`), libphonenumber picks one from the area code.
 *
 * Returns null for anything that isn't a valid number in a country the picker
 * lists, leaving the caller's defaults in place.
 */
export const fromE164 = (
  value: string,
  countries: readonly CountryConfig[],
): PhonePrefill | null => {
  const parsed = parsePhoneNumberFromString(`+${digitsOf(value)}`);
  if (parsed?.isValid() !== true || parsed.country === undefined) return null;
  const country = countries.find((c) => iso2FromFlag(c.flag) === parsed.country);
  if (country === undefined) return null;
  // `formatNational` rather than the bare national digits: it keeps the trunk
  // prefix (`07400 123456`), which the field's own formatting needs to group
  // the number the way someone in that country would type it.
  return {
    countryName: country.name,
    national: formatAsYouType(parsed.formatNational(), country),
  };
};

/**
 * The input's display value as the user types — `(212) 555-1234` for the US,
 * `01512 3456789` for Germany. Returns the raw digits for a country with no
 * numbering plan.
 */
export const formatAsYouType = (nationalInput: string, country: CountryConfig) => {
  const iso2 = parseCountry(country);
  if (iso2 === undefined) return digitsOf(nationalInput);
  return new AsYouType(iso2).input(nationalInput);
};

/** Where two strings first diverge — for one backspace, the deleted position. */
const divergenceIndex = (previous: string, edited: string) => {
  let index = 0;
  while (index < edited.length && previous[index] === edited[index]) index += 1;
  return index;
};

/**
 * The display value after the user shortens the field, given what it showed
 * beforehand.
 *
 * Reformatting the edit alone isn't enough: backspacing the `)` out of `(212)`
 * puts it straight back, so the field looks frozen. Where formatting restored
 * exactly what the keystroke removed, drop a digit instead.
 *
 * Only at the very end, though. Mid-string, `(edited, previous)` is ambiguous —
 * deleting the space from `01512 3456789` and pasting the unformatted `015123456789`
 * over it produce the identical pair, and dropping a digit would silently change
 * a number the user pasted correctly. Restoring the separator is the harmless
 * reading of the two, so ambiguity resolves that way; a mid-string backspace on
 * a separator does nothing rather than risk corrupting the value. Distinguishing
 * them needs the caret, which React Native's `onChangeText` does not carry.
 */
export const formatAfterDelete = (edited: string, previous: string, country: CountryConfig) => {
  const reformatted = formatAsYouType(edited, country);
  if (reformatted !== previous) return reformatted;
  if (divergenceIndex(previous, edited) !== previous.length - 1) return reformatted;
  return formatAsYouType(edited.replace(/\d(?=\D*$)/, ""), country);
};

/**
 * True when a proposed value carries more digits than E.164 allows. Callers drop
 * such an edit rather than truncating it, since silently dropping digits is what
 * made an 11-digit German number unenterable.
 *
 * Judged on the proposed value, never on what the field already holds: gating on
 * the latter would reject replacing a full number (select all, retype) purely
 * because the old value was at the ceiling.
 */
export const exceedsDigitLimit = (candidate: string, country: CountryConfig) => {
  // A typed `+` means the country code is already part of what was entered.
  const prefixLength = candidate.trimStart().startsWith("+") ? 0 : callingCodeOf(country).length;
  return prefixLength + digitsOf(candidate).length > E164_MAX_DIGITS;
};

/**
 * The field's next value after an edit: a deletion re-formats around the gap,
 * an addition formats as typed, and an addition past the E.164 ceiling is
 * dropped, leaving `previous`, rather than truncated (BIG-7175).
 */
export const applyPhoneEdit = (edited: string, previous: string, country: CountryConfig) => {
  if (edited.length < previous.length) return formatAfterDelete(edited, previous, country);
  if (exceedsDigitLimit(edited, country)) return previous;
  return formatAsYouType(edited, country);
};

/**
 * The region subtag of a BCP-47 tag: "de-DE" → "DE", "zh-Hans-CN" → "CN".
 * Hand-parsed rather than read off `Intl.Locale`, which Hermes does not
 * reliably expose. Returns undefined for a tag carrying no region.
 */
export const regionFromLocale = (locale: string) => {
  const subtags = locale.split("-");
  // Skip the language subtag: it is two letters too, and would otherwise read
  // as the region for every tag ("en-GB" → "EN", matching no country).
  for (const subtag of subtags.slice(1)) {
    // A one-character subtag opens an extension sequence; nothing after it is a region.
    if (subtag.length === 1) return undefined;
    if (/^[A-Za-z]{2}$/.test(subtag)) return subtag.toUpperCase();
  }
  return undefined;
};

/**
 * The device's BCP-47 locale, or undefined where `Intl` is unavailable — which
 * is the case on some Hermes builds, the runtime native ships on.
 */
export const deviceLocale = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().locale;
  } catch {
    return undefined;
  }
};

/**
 * The picker's initial country, resolved from the device's region so an
 * international user isn't left assuming their country is unsupported — the
 * impression the customer in BIG-7175 came away with. Falls back to the United
 * States when the region is unknown or absent from the list.
 *
 * `locale` is a parameter so the region rules can be tested as plain strings
 * rather than by monkey-patching the `Intl` global.
 */
export const defaultCountryName = (
  countries: readonly CountryConfig[],
  locale: string | undefined = deviceLocale(),
) => {
  const fallback = "United States";
  const region = locale === undefined ? undefined : regionFromLocale(locale);
  if (region === undefined) return fallback;
  return countries.find((country) => iso2FromFlag(country.flag) === region)?.name ?? fallback;
};
